import { groupByProp } from "../../core/utils";
import isNil from "lodash/fp/isNil";

import existingUsersCtrl from "./existingUsers/existingUsers.controller";
import existingUsersTmpl from "./existingUsers/existingUsers.html";

class Controller {
  constructor(
    $stateParams,
    Events,
    Packages,
    $mdDialog,
    $timeout,
    Lists,
    ResetFields,
    CommuterPackageTransforms
  ) {
    "ngInject";

    this.packageId = $stateParams.id;
    this.supportedRideTypes = Lists.all.supportedRideTypes;
    this.optionTypes = Lists.all.packageDiscountTypes;
    this.newOptionModel = { icon: " " };

    this.CommuterPackageTransforms = CommuterPackageTransforms;
    this.Events = Events;
    this.Package = Packages;
    this.$mdDialog = $mdDialog;
    this.$timeout = $timeout;
    this.ResetFields = ResetFields;
    this.initPage = this.initPage.bind(this);

    // Customers List
    this.customersFields = CommuterPackageTransforms.customersFields;
    this.customersFilters = CommuterPackageTransforms.customersFilters;
    this.actionListParams = { id: this.packageId, isCityRequired: false };
    this.customersActions = [
      { title: "Remove User from Package", method: this.removeUser.bind(this) },
    ];

    // Events
    this.onPackageUpdateEvent = this.Events.getAddEvent(
      "updateCommuterPackage"
    );
    this.onOptionUpdateEvent = this.Events.getAddEvent(
      "updateCommuterPackageOption"
    );
    this.onAddOptionEvent = this.Events.getAddEvent("addOption");
    this.onIconUploadEvent = this.Events.getAddEvent("optionIconUpload");
    this.onUsersUploadEvent = this.Events.getAddEvent("packageUsersUpload");
    this.editableMultipleResponseDoneEvent = this.Events.getUpdateEvent(
      "editableMultipleResponseDone"
    );
    this.errorNotificationEvent = this.Events.eventsType.onShowErrorNotification;
    this.successNotificationEvent = this.Events.eventsType.onShowSuccessNotification;

    // Customers List Events
    this.customersPaginateEvent = this.Events.getPaginateEvent(
      "packageCustomers"
    );
    this.customersReUpdateEvent = this.Events.getUpdateEvent(
      "packageCustomers"
    );

    // Lists
    this.optionTypes = this.CommuterPackageTransforms.optionTypes;
    this.addOptions = this.CommuterPackageTransforms.addOptions;
  }

  $onInit() {
    this.Events.on(this.onPackageUpdateEvent, this.onPackageUpdate.bind(this));
    this.Events.on(this.onOptionUpdateEvent, this.onOptionUpdate.bind(this));
    this.Events.on(this.onAddOptionEvent, this.onAddOption.bind(this));
    this.Events.on(this.onIconUploadEvent, this.replaceOption.bind(this));
    this.Events.on(
      this.onUsersUploadEvent,
      this.onCustomersReUpdate.bind(this)
    );

    this.Package.get(this.packageId).$promise.then(this.initPage, (response) =>
      this.Events.emit(this.errorNotificationEvent, response.data)
    );
  }

  $onDestroy() {
    this.$mdDialog.hide();

    this.Events.off(this.onPackageUpdateEvent, this.onPackageUpdate);
    this.Events.off(this.onOptionUpdateEvent, this.onOptionUpdate);
    this.Events.off(this.onAddOptionEvent, this.onAddOption);
    this.Events.off(this.onIconUploadEvent, this.replaceOption);
    this.Events.off(this.onUsersUploadEvent, this.onCustomersReUpdate);
  }

  initPage(response) {
    this.package = this.CommuterPackageTransforms.justify(response);

    this.addOptions.fields[0].currentCity = this.package.city;
    this.addOptions.fields[1].currentCity = this.package.city;

    this.customersFilters[0].resourceParams = this.actionListParams;
  }

  onPackageUpdate(item, option = "", crudAction = "update") {
    let updatedItem;
    const formatKey = (key = "Fields") => {
      return key
        .split("_")
        .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
        .join(" ");
    };
    if (isNil(item.key)) {
      updatedItem = item; // Pass the whole object
    } else {
      updatedItem = {
        [item.key]: !isNil(item._id)
          ? item._id
          : isNil(item.value)
          ? null
          : item.value, // Prioritize item id over item value
      };
    }
    this.Package[crudAction](
      `${this.packageId}${option}`,
      updatedItem
    ).$promise.then(
      (response) => {
        if (!option) {
          // Package update
          this.initPage(response);
        } else if (option) {
          // Option update
          this.replaceOption(response);
        }
        if (crudAction === "addOption") {
          this.addOptions.fields = this.ResetFields.reset(
            this.addOptions.fields
          );
          item.key = "Options";
        }
        this.Events.emit(this.successNotificationEvent, {
          message: `${formatKey(item.key)} has been updated successfully`,
        });
      },
      (response) =>
        this.Events.emit(this.errorNotificationEvent, {
          message: `Sorry action failed due to: ${response.statusText}`,
        })
    );
  }

  replaceOption(response) {
    const optionIndex = this.package.package_options.findIndex(
      (option) => option.id.value === response.id
    );
    const justifiedOption = this.CommuterPackageTransforms.justifyOptions(
      response
    );
    if (optionIndex > -1) {
      this.package.package_options.splice(optionIndex, 1, justifiedOption);
      this.$timeout(() => {
        // Time for splice to be executed
        this.Events.emit(this.editableMultipleResponseDoneEvent);
      }, 0);
    } else {
      this.package.package_options.push(justifiedOption);
    }
  }

  onOptionUpdate(item) {
    const { methodName: optionId } = item;
    this.onPackageUpdate(item, `/options/${optionId}`);
  }

  onAddOption(item) {
    this.onPackageUpdate({ ...item }, "/options/", "addOption");
  }

  removeUser(user) {
    switch (user.subscription_state) {
      case "Subscribed":
        this.Events.emit(this.errorNotificationEvent, {
          message: `User "${user.name}" is subscribed, cannot remove!`,
        });
        break;
      case "Pending":
        this.Events.emit(this.errorNotificationEvent, {
          message: `User "${user.name}" is in pending payment, cannot remove!`,
        });
        break;
      default:
        this.Package.removeUser(this.packageId, [user._id]).$promise.then(
          () => {
            delete this.actionListParams.user_id;
            this.Events.emit(this.customersReUpdateEvent);
            this.Events.emit(this.successNotificationEvent, {
              message: "User is removed successfully",
            });
          },
          (response) =>
            this.Events.emit(this.errorNotificationEvent, {
              message: `User can't be removed due to: ${response.statusText}`,
            })
        );
    }
  }

  onCustomersReUpdate(response) {
    this.Events.emit(this.customersReUpdateEvent);
    if (response.already_exist.length > 0) {
      const groupedUsers = groupByProp(response.already_exist, "package");
      const alertDialog = {
        controller: existingUsersCtrl,
        controllerAs: "$ctrl",
        template: existingUsersTmpl,
        locals: { users: groupedUsers, packageId: this.packageId },
        clickOutsideToClose: true,
      };
      this.$mdDialog.show(alertDialog);
    }
  }

  exportCSV(packageId, option) {
    let optionIdUrl = "";
    if (!isNil(option)) {
      optionIdUrl = `options/${option.id.value}/`;
    }
    this.Package.exportCSV(packageId, optionIdUrl).$promise.then(
      (response) => {
        const { status } = response;
        if (status === 204) {
          // 204 No Content
          return this.Events.emit(this.errorNotificationEvent, {
            message: "Sorry no customers are subscribed to this option.",
          });
        }
        this.Events.emit(this.successNotificationEvent, {
          message:
            "Your request is being processed. A link to the file will be sent to your email in the next few moments.",
        });
      },
      (response) => this.Events.emit(this.errorNotificationEvent, response.data)
    );
  }

  // Activation/ Highlight
  handlePreStepActivation(option, optionAction) {
    let action;
    let actionStatus;
    let item;
    if (option) {
      option[optionAction].value = !option[optionAction].value; // Prevent default switch behavior
      item = "Option";
      actionStatus = this.getOptionStatus(option, optionAction);
      action = `options/${option.id.value}/${actionStatus}`;
    } else {
      this.package.is_active.value = !this.package.is_active.value; // Prevent default switch behavior
      item = "Package";
      actionStatus = this.package.is_active.value ? "deactivate" : "activate";
      action = actionStatus;
    }
    const confirm = this.$mdDialog
      .confirm()
      .title(`Are you sure you want to ${actionStatus} this package ?`)
      .ok("Confirm")
      .cancel("Cancel");

    this.$mdDialog.show(confirm).then(() => {
      // On modal Confirm
      this.Package.update(`${this.packageId}/${action}`).$promise.then(
        (response) => {
          // Response success
          if (!option) {
            this.initPage(response);
          } else if (option) {
            this.replaceOption(response);
          }
          this.Events.emit(this.successNotificationEvent, {
            message: `${item} is ${actionStatus}d successfully`,
          });
        },

        (response) => {
          // Response error
          this.Events.emit(this.errorNotificationEvent, {
            message: response.data.message,
          });
        }
      );
    });
  }

  getOptionStatus(option, optionAction) {
    const isTrue = option[optionAction].value;
    switch (optionAction) {
      case "is_active":
        return isTrue ? "deactivate" : "activate";
      case "is_highlighted":
        return isTrue ? "unhighlight" : "highlight";
      default:
        return "undefined";
    }
  }
  // End of Activation/ Highlight
}

export default Controller;



// WEBPACK FOOTER //
// ./src/pages/commuterPackage/commuterPackage.controller.js